import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { createPartnersApi, getPartnersList } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Partner scope resolution for the internal partner switcher (SOPHIA-35095).
 *
 * Internal users (sophia_partner_admin / sophia_admin) carry no partnerId
 * claim; they pick the partner they are viewing here and every data call
 * sends it as X-Partner-Id, which the data APIs honor for these roles only.
 * Partner users are always scoped to their token's claim — the switcher is
 * never shown to them and their selection state is ignored.
 */

/** Mirrors PARTNER_OVERRIDE_ROLES in the data APIs' auth middleware. */
const INTERNAL_SWITCHER_ROLES = ['sophia_partner_admin', 'sophia_admin'];

const SELECTED_PARTNER_STORAGE_KEY = 'sophia_pp_selected_partner';

export interface PartnerOption {
  id: number;
  name: string;
  /** False when the partner is deactivated in the LMS. */
  active: boolean;
}

interface PartnerScope {
  /** True when the signed-in user may switch partners. */
  isInternal: boolean;
  /** The partner every data call should be scoped to; null = nothing selected. */
  activePartnerId: number | null;
  selectedPartnerId: number | null;
  setSelectedPartnerId: (id: number | null) => void;
  /** Switcher options; only fetched for internal users. */
  partners: PartnerOption[];
  partnersLoading: boolean;
}

const PartnerScopeContext = createContext<PartnerScope | undefined>(undefined);

function readStoredSelection(): number | null {
  try {
    const raw = sessionStorage.getItem(SELECTED_PARTNER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
  } catch {
    return null;
  }
}

export function PartnerScopeProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { idToken, partnerId: claimPartnerId, roles } = useAuth();

  const isInternal = INTERNAL_SWITCHER_ROLES.some((role) => roles.includes(role));

  const [selectedPartnerId, setSelectedPartnerIdState] = useState<number | null>(() => {
    return readStoredSelection() ?? null;
  });
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  const setSelectedPartnerId = (id: number | null) => {
    setSelectedPartnerIdState(id);
    try {
      if (id == null) {
        sessionStorage.removeItem(SELECTED_PARTNER_STORAGE_KEY);
      } else {
        sessionStorage.setItem(SELECTED_PARTNER_STORAGE_KEY, String(id));
      }
    } catch {
      // Session persistence is best-effort only
    }
  };

  useEffect(() => {
    if (!isInternal || !idToken) return;

    let cancelled = false;
    setPartnersLoading(true);
    const client = createPartnersApi(idToken);
    (async () => {
      try {
        // The API caps per_page at 100 and there are more partners than that,
        // so page through the whole list. status=all so users claimed to a
        // deactivated partner still resolve to a name (the switcher filters
        // back down to active partners).
        const all: PartnerOption[] = [];
        let page = 1;
        let total = Infinity;
        while (all.length < total && page <= 20) {
          const data = await getPartnersList({ per_page: 100, page, status: 'all' }, client);
          total = data.total;
          all.push(
            ...data.items
              .map((item) => ({
                id: Number(item.partner_id),
                name: item.institution_name,
                active: item.status !== 'inactive',
              }))
              .filter((item) => item.id > 0)
          );
          if (data.items.length === 0) break;
          page += 1;
        }
        if (!cancelled) setPartners(all);
      } catch (err) {
        console.error('[PartnerScope] Failed to fetch partner list:', err);
      } finally {
        if (!cancelled) setPartnersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInternal, idToken]);

  // Internal users resolve to their selection (falling back to a claim if they
  // happen to have one); partner users are always locked to their claim.
  const activePartnerId = isInternal ? (selectedPartnerId ?? claimPartnerId) : claimPartnerId;

  const value = useMemo(
    () => ({
      isInternal,
      activePartnerId,
      selectedPartnerId,
      setSelectedPartnerId,
      partners,
      partnersLoading,
    }),
    [isInternal, activePartnerId, selectedPartnerId, partners, partnersLoading]
  );

  return <PartnerScopeContext.Provider value={value}>{children}</PartnerScopeContext.Provider>;
}

export function usePartnerScope(): PartnerScope {
  const context = useContext(PartnerScopeContext);
  if (context === undefined) {
    throw new Error('usePartnerScope must be used within a PartnerScopeProvider');
  }
  return context;
}
